import Box from '@mui/material/Box';
import { Form, Formik } from 'formik';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { selectConfigurationParameterById } from '!core/redux';
import { accountBalanceSelectedSelector } from '!core/redux/accountBalance/accountBalance.selectors';
import { isOtpActive } from '!core/redux/dbConfigurations';
import { securityAccountSelectedSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { ButtonNav } from '»components/atoms/Buttons';
import { TrlStepper } from '»components/molecules/TrlStepper';
import { useCurrentPage, useHandleSagaError } from '»utils/customHooks';
import { addDays, dateToString } from '»utils/date';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { PrivateView } from '../../PrivateView';
import {
  insertOrder,
  orderValidationCalcComissions,
  replaceOrder,
  validateCode,
} from './TrlInsertOrderPrimary.helper';
import { insertOrderPrimarySlice } from './trlInsertOrderPrimary.slice';
import { GetStepContent } from './TrlInsertOrderPrimary.steps';
import { handleComplete } from './TrlInsertOrderPrimary.steps.helper';
import {
  ActionContainer,
  StepperBoxOut,
  StepperContainerStyled,
} from './TrlInsertOrderPrimary.styled';
import { trlInsertOrderPrimaryValidation } from './TrlInsertOrderPrimary.yup';

export const TrlInsertOrderPrimary = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [isValidRequest, setIsValidRequest] = useState(true);
  const [invalidRequestReason, setInvalidRequestReason] = useState(null);

  const [ordQuantity, setOrdQuantity] = useState(null);
  const [ordPrice, setOrdPrice] = useState(null);
  const [orderComissions, setOrderComissions] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const securityAccount = useSelector(securityAccountSelectedSelector);
  const currentAccount = useSelector(accountBalanceSelectedSelector);

  const { value: addExpiredDays } = useSelector((state) =>
    selectConfigurationParameterById(state, 'PRIM_EXPIRE_TIME'),
  );

  const { isFundsListPage } = useCurrentPage();
  const { handleSagaError } = useHandleSagaError();

  const useOtpForPrimaryMarket = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_PRIMARY_ORDER'),
  );
  const useOtpForFundsPrimary = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_FUNDS_PRIMARY'),
  );

  const useOtp = isFundsListPage
    ? useOtpForFundsPrimary
    : useOtpForPrimaryMarket;

  const steps = [
    TrlFormattedMessagePlain({ id: 'TRLT_UI-INTENTION' }),
    TrlFormattedMessagePlain({ id: 'TRLT_UI-SIMULATION' }),
    TrlFormattedMessagePlain({ id: 'TRLT_UI-CONFIRMATION' }),
  ];

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsValidRequest(false);
    setInvalidRequestReason(null);

    const { inputQuantity, inputPrice, validationCode } = values;

    setOrdQuantity(inputQuantity);
    setOrdPrice(inputPrice);

    const {
      securityExchange,
      securityType,
      symbol,
      currency,
      publicOfferId,
      trancheId,
      trancheType,
      operationDate,
      subscriptionPrice,
    } = location.state.dataItem;

    const priceToSubmit = isFundsListPage ? subscriptionPrice : inputPrice;

    if (activeStep === 0) {
      const orderValidationCalcComissionsResp =
        await orderValidationCalcComissions({
          publicOfferId,
          trancheId,
          orderQty: inputQuantity,
          price: priceToSubmit,
          clientId: securityAccount,
          cashAccount: currentAccount,
          securityExchange,
          securityType,
          symbol,
          currency,
          side: isFundsListPage ? 'D' : '1',
          doValidation: !isFundsListPage,
        });
      const { isValid, comissions, reason } = orderValidationCalcComissionsResp;
      if (isValid) {
        setIsValidRequest(isValid);
        setOrderComissions(comissions);
        handleComplete({
          steps,
          completed,
          activeStep,
          setCompleted,
          setActiveStep,
        });
        setSubmitting(false);
      } else {
        setIsValidRequest(isValid);
        setInvalidRequestReason(reason);
        setSubmitting(false);
      }
    } else {
      const [{ simulationId }] = orderComissions;
      let insertOrderResp;
      let canExecuteOrder = true;

      if (useOtp)
        canExecuteOrder = await validateCode({
          validationCode,
          setIsValidRequest,
          setInvalidRequestReason,
          handleSagaError,
        });

      if (canExecuteOrder) {
        if (!location.state.edit) {
          insertOrderResp = await insertOrder({
            simulationId,
            clientId: securityAccount,
            cashAccount: currentAccount,
            securityExchange,
            securityType,
            symbol,
            orderQty: inputQuantity,
            currency,
            price: priceToSubmit,
            ordType: isFundsListPage || trancheType === 'B' ? '2' : '1',
            timeInForce: isFundsListPage ? 1 : 6,
            expireTime: isFundsListPage
              ? dateToString(new Date())
              : dateToString(addDays(new Date(operationDate), addExpiredDays)),
            confirmSeq: orderStatus && orderStatus[0].confirmSeq,
          });
        } else {
          insertOrderResp = await replaceOrder({
            simulationId,
            clientId: securityAccount,
            ordBookKey: location.state.dataItem.ordBookKey,
            orderQty: inputQuantity,
            price: priceToSubmit,
            confirmSeq: orderStatus && orderStatus[0].confirmSeq,
          });
        }

        const { isValid, order, reason } = insertOrderResp;
        if (isValid) {
          setIsValidRequest(isValid);
          setOrderStatus(order);
          handleComplete({
            steps,
            completed,
            activeStep,
            setCompleted,
            setActiveStep,
          });
        } else {
          setIsValidRequest(isValid);
          setInvalidRequestReason(reason);
          if (order) setOrderStatus(order);
        }
      }

      setSubmitting(false);
    }
  };

  const defineInitialPrice = () => {
    let initPrice = ordPrice;

    const {
      state: {
        edit,
        dataItem: { price, orderPrice, trancheType },
      },
    } = location;

    if (edit) {
      initPrice = orderPrice;
    } else if (trancheType === 'B') {
      if (price) initPrice = price;
    }

    return initPrice;
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(insertOrderPrimarySlice.actions.insertOrderPrimaryActive());

    return () => {
      dispatch(insertOrderPrimarySlice.actions.insertOrderPrimaryInactive());
    };
  }, [dispatch]);

  const getGoBackBtnDetails = () => {
    if (isFundsListPage)
      return {
        goBackBtnName: 'TRLT_UI-FUNDS_LIST',
        previousPageLink: '/Market/Funds',
      };

    if (location.state.edit)
      return {
        goBackBtnName: 'TRLT_UI-ORDERS_LIST',
        previousPageLink: '/Trading/Primary',
      };

    return {
      goBackBtnName: 'TRLT_UI-ISSUANCE_LIST',
      previousPageLink: '/Market/Primary',
    };
  };

  const { goBackBtnName, previousPageLink } = getGoBackBtnDetails();

  const validationData = {
    minAmount:
      location.state.dataItem.minAmount ||
      location.state.dataItem.initialAmount,
    maxAmount: location.state.dataItem.maxAmount,
    minQuantity: location.state.dataItem.minQuantity,
    maxQuantity: location.state.dataItem.maxQuantity,
    trancheType: location.state.dataItem.trancheType,
    price: location.state.dataItem.price,
    minPrice: location.state.dataItem.minPrice,
    offerMinPrice: location.state.dataItem.offerMinPrice,
    maxPrice: location.state.dataItem.maxPrice,
    offerMaxPrice: location.state.dataItem.offerMaxPrice,
    minimumBatch: location.state.dataItem.minimumBatch,
    subscriptionPrice: location.state.dataItem.subscriptionPrice,
    initialAmount: location.state.dataItem.initialAmount,
    quantityPrecision: location.state.dataItem.quantityPrecision,
  };

  return (
    <StepperContainerStyled>
      <TrlStepper activeStep={activeStep} steps={steps} completed={completed} />
      <PrivateView>
        <StepperBoxOut>
          <Box>
            <ActionContainer>
              <Formik
                initialValues={{
                  inputQuantity: location.state.edit
                    ? location.state.dataItem.orderQty
                    : ordQuantity,
                  inputPrice: defineInitialPrice(),
                  validationCode: '',
                }}
                onSubmit={handleSubmit}
                validationSchema={trlInsertOrderPrimaryValidation({
                  dataItem: validationData,
                  activeStep,
                  useOtp,
                  isFundsListPage,
                })}
              >
                {({ ...rest }) => (
                  <Form>
                    {GetStepContent({
                      step: activeStep,
                      validRequest: isValidRequest,
                      invalidReason: invalidRequestReason,
                      orderStatus,
                      steps,
                      location,
                      activeStep,
                      setActiveStep,
                      orderComissions,
                      symbol: location.state.dataItem.symbol,
                      currency: location.state.dataItem.currency,
                      ...rest,
                    })}
                  </Form>
                )}
              </Formik>
            </ActionContainer>
          </Box>
          <ButtonNav
            sx={{ position: 'absolute' }}
            expressionId={goBackBtnName}
            onClick={() => {
              navigate(previousPageLink);
            }}
          />
        </StepperBoxOut>
      </PrivateView>
    </StepperContainerStyled>
  );
};
